//@flow
import * as React from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import ReactTable from 'react-table';
import withFixedColumns from 'react-table-hoc-fixed-columns';
import { themr } from 'react-css-themr';
import Icon, { NeoIconWithPopover } from 'common/components/core/icon';
import { ButtonWithPopover, ButtonTypes } from 'common/components/core/button';
import {
    PopoverTriggerEnum,
    PopoverPlaceEnum
} from 'common/components/core/popover/popOverFactory';
import PopOverMenu from 'common/components/core/form/PopOverMenu';
import DefaultFilter from 'common/entity/components/DefaultFilter';
import { Loader } from 'common/components/core/loader/Loader';
import PageChooser from './PageChooser';
import TimeUtils from 'common/utils/TimeUtils';
import NumberUtils from 'common/utils/NumberUtils';
import DataUtils from 'common/utils/DataUtils';
import { DivLoader } from 'common/components/core/wrappers/Div';
import PaginatedTableInputField from 'common/components/core/table/PaginatedTableInputField';

import { CellDataTypes } from './CellDataTypes';
import theme from './PaginatedTable.scss';
import { withUnitConverter } from 'common/containers/intl';
import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';
import type { TFunction } from 'react-i18next';
import Units from 'common/enums/UnitEnum';
import entityIds from 'common/tests/entity/entityIds.json';
import { Icon as NeoIcon, Columns } from '@locus-taxy/neo-ui';
import StopPropagation from 'common/components/core/StopPropagation';
import coreIds from 'common/tests/core/coreIds.json';
import { type LocaleSettings } from 'common/models/internal/LocaleSettings';
import ErrorReporter from 'common/utils/ErrorReporter';
import EnvConfig from 'config';
import DateTimeUtils, {
    DateStyle,
    TimeStyle
} from 'common/utils/DateTimeUtils';

export const DEFAULT_PAGE_SIZE = 15;
export const StickyPositionEnum = {
    Right: 'right',
    Left: 'left'
};

export const FROZEN_COLUMN_LIMIT = 3;

const ReactTableFixedColumns = withFixedColumns(ReactTable);

type Props = {
    data: Array<any>,
    totalPages: ?number,
    clientId?: string,
    selectedPrimaryColumnValue?: string,
    appliedFilters: { string: any },
    theme: Object,
    columns: Array<Object>,
    enableFiltering: boolean,
    hasCheckbox?: boolean,
    className?: string,
    unitConverter: UnitConversionHelper,
    getTrProps?: (Object, Object, Object, Object) => Object,
    onTableStateChange: ({
        pageSize: number,
        page: number,
        sorted: Array<Object>,
        filtered: Object
    }) => void,
    onPageChange: number => void,
    onPageSizeChange: number => void,
    onFilteredChange: () => void,
    getIsRowLoading?: (Object, string) => boolean,
    onSortedChange?: Function,
    onColumnFreeze: Function,
    freezeInfo: Array<any>,
    hasFreezableColumns: boolean,
    frozenColumnLimit: number,
    pinTooltip: string,
    unpinTooltip: string,
    wrapperClass?: string,
    selectedRows: Array<string>,
    stickyHeader?: boolean,
    t: TFunction,
    pageIndex?: number,
    pageSizeOptions?: Array<{ value: number, label: any }>,
    extraHeaderProps?: any,
    showHeaderDropDownIcon: boolean,
    renderHeaderMenu: any => React.Element<$FlowFixMeProps>,
    NoDataComponent?: React.Component<$FlowFixMeProps>,
    isCellEditable?: (tdProps: Object) => boolean,
    manual?: boolean,
    preserveHeaderTextCase: boolean,
    pageSize?: number,
    localeSettings?: LocaleSettings,
    // Clean up this `useLocalisation` while releasing the TrackIQ localisation changes
    useLocalisation?: boolean
};

type State = {
    pageSize: number,
    sorted: Array<Object>,
    selectedPageIndex: number,
    scrollToTop: boolean,
    activeColumn: ?string
};

type HeaderPropsType = {
    column: {
        headerText: (params: HeaderPropsType) => React.Node | string,
        id: string,
        title?: string,
        menu: Array<$FixMe>,
        sortable: boolean,
        disablePinControl: boolean,
        columnId: string,
        headerIcon?: string,
        headerTextId: string,
        showIconOnly?: boolean,
        isFilterable?: boolean,
        accessorString: string
    }
};

const DefaultLoadingComponent = ({ loading, theme, loaderSize }) => {
    return (
        <Loader
            active={loading}
            className={theme.tableLoader}
            size={loaderSize}
        />
    );
};
const ThemedDefaultLoadingComponent = themr(
    'LoadingComponent',
    theme
)(DefaultLoadingComponent);

const paginatedTableFactory = ({ TableComponent }) => {
    class PaginatedTable extends React.PureComponent<Props, State> {
        static defaultProps = {
            defaultDateFormat: 'Do MMM YYYY',
            LoadingComponent: ThemedDefaultLoadingComponent,
            freezeInfo: [],
            extraHeaderProps: {},
            appliedFilters: {},
            stickyHeader: true,
            showHeaderDropDownIcon: false,
            preserveHeaderTextCase: false,
            useLocalisation: false,
            'data-test-id': coreIds.paginatedTable
        };

        constructor(props) {
            super(props);
            const { pageIndex = 0, defaultPageSize } = props;
            this.state = {
                sorted: [],
                selectedPageIndex: pageIndex,
                scrollToTop: false,
                pageSize: defaultPageSize || DEFAULT_PAGE_SIZE,
                activeColumn: null
            };

            this.rtTable = React.createRef();
        }

        componentDidUpdate(prevProps) {
            const { scrollToTop } = this.state;
            const { pageIndex, pageSize } = this.props;
            // this is for scroll to top when a new filter is applied
            if (scrollToTop) {
                this.setState({ scrollToTop: false });
                const { current: tableDOM } = this.rtTable;
                // direct DOM access
                tableDOM && (ReactDOM.findDOMNode(tableDOM).scrollTop = 0); // setting scroll top value to react-table
            }
            if (
                pageIndex !== prevProps.pageIndex ||
                pageSize !== prevProps.pageSize
            ) {
                this.setState({ selectedPageIndex: pageIndex, pageSize });
            }
        }

        getTableProps = () => {
            return {
                ref: this.rtTable
            };
        };

        onSortedChange = sorted => {
            const { onSortedChange } = this.props;
            this.setState({ sorted });
            onSortedChange && onSortedChange({ sorted });
        };

        getSortIcon = ({ id, sortable }) => {
            if (this.props.showHeaderDropDownIcon) {
                return null;
            }
            const sortInfo = this.state.sorted.filter(item => item.id === id);
            const upIcon = (
                <Icon
                    theme={theme}
                    className={classnames(theme.sortIcon, theme.upIcon)}
                    icon="caret_up"
                />
            );
            const downIcon = (
                <Icon
                    theme={theme}
                    className={classnames(theme.sortIcon, theme.downIcon)}
                    icon="caret_down"
                />
            );
            if (sortInfo.length) {
                if (sortInfo[0].desc) {
                    return upIcon;
                } else {
                    return downIcon;
                }
            } else if (sortable !== false) {
                return (
                    <div className={theme.sortableIcon}>
                        {upIcon}
                        {downIcon}
                    </div>
                );
            }
            return null;
        };

        renderMenu = ({ menu }) => {
            const { theme, selectedRows } = this.props;
            if (menu && menu.length) {
                return (
                    <StopPropagation eventTypes="onClick">
                        <PopOverMenu
                            theme={theme}
                            menu={menu}
                            disabled={!selectedRows || !selectedRows.length}
                        />
                    </StopPropagation>
                );
            }
            return null;
        };

        getFilterCell = filterProps => {
            const { filter, onChange } = filterProps;
            return (
                <DefaultFilter
                    filter={filter}
                    onChange={onChange}
                    className={theme.filterTextBox}
                />
            );
        };

        renderHeaderIcon = ({ headerIcon }) => {
            if (headerIcon) {
                return (
                    <NeoIcon icon={headerIcon} color="grey.600" size="medium" />
                );
            }
            return null;
        };

        renderHeaderDropDownMenu = column => {
            return this.props.renderHeaderMenu({
                column,
                onDismiss: this.onDismissHeaderDropDownMenu
            });
        };

        onDismissHeaderDropDownMenu = () => {
            this.setState({ activeColumn: null });
        };

        onDropdownIconClick = e => {
            this.setState({ activeColumn: e.target.id });
        };

        renderHeaderDropDownIcon = column => {
            const {
                isFilterable,
                sortable,
                accessorString,
                isActive,
                headerTextId
            } = column;
            const {
                sortingInfo,
                appliedFilters,
                showHeaderDropDownIcon
            } = this.props;
            if (showHeaderDropDownIcon && (isFilterable || sortable)) {
                const isFilterApplied =
                    sortingInfo?.[0]?.name === accessorString ||
                    appliedFilters[headerTextId];
                return (
                    <NeoIconWithPopover
                        icon={isActive ? 'arrow_drop_up' : 'arrow_drop_down'}
                        color={isFilterApplied ? 'blue.500' : 'grey.600'}
                        isActive={isActive}
                        size="medium"
                        preferPlace={PopoverPlaceEnum.Below}
                        isPopOverOpen={isActive}
                        className={theme.dropdownIcon}
                        popOverClassName={theme.columnDropdown}
                        id={headerTextId}
                        onClick={this.onDropdownIconClick}
                        popOverContent={this.renderHeaderDropDownMenu(column)}
                        invertPopover={false}
                        disableArrow
                    />
                );
            }
            return null;
        };

        getHeaderCell = (headerProps: HeaderPropsType) => {
            const {
                theme,
                extraHeaderProps,
                preserveHeaderTextCase
            } = this.props;
            const { activeColumn } = this.state;
            const { column } = headerProps;
            const {
                headerText,
                title,
                id,
                menu,
                sortable,
                disablePinControl,
                columnId,
                headerIcon,
                headerTextId,
                showIconOnly
            } = column;
            let header;
            const cellTestId = `${headerTextId}-header`;
            const cellActionsTestId = `${headerTextId}-header-actions`;
            if (headerText) {
                if (typeof headerText === 'string') {
                    header = preserveHeaderTextCase
                        ? headerText
                        : headerText.toUpperCase();
                } else {
                    header = headerText({
                        ...headerProps,
                        ...extraHeaderProps
                    });
                }
            }
            const sanitizedHeader = typeof header === 'string' ? header : null;

            const tooltip = !extraHeaderProps.hideDivTitle
                ? title || sanitizedHeader
                : null;
            const isColumnDropdownActive = activeColumn === headerTextId;
            return header || headerIcon ? (
                <div
                    className={classnames(theme.headerCell, {
                        [theme.active]: isColumnDropdownActive
                    })}
                    id={headerTextId}
                    title={tooltip}
                >
                    <div data-test-id={cellTestId} className={theme.headerText}>
                        <Columns align="center">
                            {this.renderHeaderIcon({
                                headerIcon
                            })}
                        </Columns>
                        {!showIconOnly && header}
                    </div>
                    <div
                        className={theme.optionIcons}
                        data-test-id={cellActionsTestId}
                    >
                        {this.renderPinIcon({
                            columnId,
                            disablePinControl
                        })}
                        {this.getSortIcon({ id, sortable })}
                        {menu ? this.renderMenu({ menu }) : []}
                        {this.renderHeaderDropDownIcon({
                            ...column,
                            isActive: isColumnDropdownActive
                        })}
                    </div>
                </div>
            ) : null;
        };

        onPinHandler = (id, e) => {
            const { onColumnFreeze } = this.props;
            e.stopPropagation();
            onColumnFreeze(id, false);
        };

        onUnpinHandler = (id, e) => {
            const { onColumnFreeze } = this.props;
            e.stopPropagation();
            onColumnFreeze(id, true);
        };

        renderPinIcon = ({ columnId, disablePinControl }) => {
            const {
                freezeInfo,
                hasFreezableColumns,
                frozenColumnLimit: providedFrozenColumnLimit,
                pinTooltip,
                unpinTooltip
            } = this.props;
            const frozen = freezeInfo.some(_columnId => _columnId === columnId);
            const frozenColumnLimit =
                providedFrozenColumnLimit !== undefined
                    ? providedFrozenColumnLimit
                    : FROZEN_COLUMN_LIMIT;
            if (disablePinControl || !hasFreezableColumns) {
                return null;
            }
            if (!frozen && !(freezeInfo.length < frozenColumnLimit)) {
                return null;
            } else if (frozen) {
                return (
                    <ButtonWithPopover
                        buttonType={ButtonTypes.Icon}
                        fieldId={columnId}
                        icon="pin"
                        theme={theme}
                        className={theme.pinIcon}
                        onClick={this.onPinHandler}
                        popOverContent={
                            <span className={theme.message}>
                                {unpinTooltip}
                            </span>
                        }
                        triggerPopoverOn={PopoverTriggerEnum.Hover}
                        place={PopoverPlaceEnum.Above}
                    />
                );
            } else if (freezeInfo.length < frozenColumnLimit) {
                return (
                    <ButtonWithPopover
                        buttonType={ButtonTypes.Icon}
                        icon="unpin"
                        fieldId={columnId}
                        theme={theme}
                        className={theme.unpinIcon}
                        onClick={this.onUnpinHandler}
                        popOverContent={
                            <span className={theme.message}>{pinTooltip}</span>
                        }
                        triggerPopoverOn={PopoverTriggerEnum.Hover}
                        place={PopoverPlaceEnum.Above}
                    />
                );
            }
        };

        getColumnFilterCell(column) {
            const { FilterComponent } = column;
            return FilterComponent
                ? this.getFilterComponentCell
                : this.getFilterCell;
        }

        getFilterComponentCell({ filter, onChange, column }) {
            const options =
                column.getFilterOptions && column.getFilterOptions({ column });
            const { FilterComponent } = column;
            const filterProps = column.getExtraFilterProps
                ? column.getExtraFilterProps()
                : {};
            return (
                <FilterComponent
                    className={theme.filterTextBox}
                    theme={theme}
                    onChange={onChange}
                    options={options}
                    value={filter ? filter.value : null}
                    {...filterProps}
                />
            );
        }

        baseCellRenderer = params => {
            const { column, row, original, tdProps } = params;
            const {
                defaultDateFormat,
                getIsRowLoading,
                unitConverter,
                selectedDate,
                t,
                theme,
                handlePartialEntityUpdate,
                isCellEditable,
                handleBulkEntityUpdate,
                readOnly,
                localeSettings,
                useLocalisation
            } = this.props;
            const {
                dataType,
                accessor,
                id,
                dateFormat,
                excludeFromLoading,
                isEditable,
                isBulkEditable,
                placeholder,
                headerTextId,
                timeUnits = ['h', 'm'],
                showZeroIfNull
            } = column;
            const data = row[id || accessor];
            let cellText = data;
            const cellTestId = `${headerTextId}-${row._index}`;
            const isLoading =
                getIsRowLoading && getIsRowLoading(original, dataType);

            if (isLoading && !excludeFromLoading) {
                return <DivLoader loading theme={theme} />;
            }

            if (column.CustomCellRenderer) {
                const customCellRendererNode = column.CustomCellRenderer(
                    params
                );
                const isLocalEnv = EnvConfig.env === 'local';
                if (customCellRendererNode === undefined && !isLocalEnv) {
                    ErrorReporter.notify(
                        new Error(
                            `CustomCellRenderer for column ${id} returned nothing. Please return a valid React node.`
                        )
                    );
                    return null;
                } else {
                    return customCellRendererNode;
                }
            }
            let isDateFormatMMDDYYYY = false;
            if (localeSettings) {
                isDateFormatMMDDYYYY = localeSettings.isDateFormatMMDDYYYY();
            }

            switch (dataType) {
                case CellDataTypes.LatLng:
                    if (!data) {
                        cellText = '-';
                    } else {
                        cellText = `(${Number(data.lat).toFixed(4)}, ${Number(
                            data.lng
                        ).toFixed(4)})`;
                    }
                    break;
                case CellDataTypes.Date:
                    cellText = TimeUtils.format(
                        data,
                        dateFormat || defaultDateFormat
                    );
                    if (useLocalisation) {
                        cellText = DateTimeUtils.format(data, {
                            dateStyle: DateStyle.MEDIUM
                        });
                    }
                    break;
                case CellDataTypes.DateTime:
                    cellText = TimeUtils.formatTime(
                        data,
                        undefined,
                        undefined,
                        undefined,
                        isDateFormatMMDDYYYY
                    );
                    if (useLocalisation) {
                        cellText = DateTimeUtils.format(data, {
                            dateStyle: DateStyle.MEDIUM,
                            timeStyle: TimeStyle.SHORT
                        });
                    }
                    break;
                case CellDataTypes.Time:
                    cellText = TimeUtils.formatTime(
                        data,
                        selectedDate,
                        undefined,
                        undefined,
                        isDateFormatMMDDYYYY
                    );
                    if (useLocalisation) {
                        cellText = DateTimeUtils.formatTime(data, {
                            dateToCompare: selectedDate
                        });
                    }
                    break;
                case CellDataTypes.HumanizedTime:
                    cellText = TimeUtils.humanizeSeconds(data, {
                        units: timeUnits,
                        largest: 2
                    });
                    break;
                case CellDataTypes.Number:
                    cellText = NumberUtils.roundOffWithNonZeroDecimal(data);
                    break;
                case CellDataTypes.Percentage:
                    cellText = !DataUtils.isEmpty(data)
                        ? `${NumberUtils.roundOffWithNonZeroDecimal(data)} %`
                        : '';
                    break;
                case CellDataTypes.Distance: {
                    const preferredUnits = [
                        Units.list.length.Kilometer,
                        Units.list.length.Mile
                    ];
                    const {
                        value: lengthInUnit,
                        unit
                    } = unitConverter.convertMeters({
                        value: data,
                        preferredUnits,
                        pluralizeUnit: false
                    });
                    const placeholder = showZeroIfNull
                        ? `0 ${t(`units.${unit}`)}`
                        : '-';
                    cellText = !DataUtils.isEmpty(lengthInUnit)
                        ? `${NumberUtils.roundOffWithNonZeroDecimal(
                              lengthInUnit
                          )} ${t(`units.${unit}`)}`
                        : placeholder;
                }
            }
            if (
                !readOnly &&
                (isEditable || (isCellEditable && isCellEditable(tdProps)))
            ) {
                return (
                    <PaginatedTableInputField
                        original={original}
                        placeholder={placeholder}
                        column={column}
                        theme={theme}
                        testId={cellTestId}
                        onSave={handlePartialEntityUpdate}
                        onBulkSave={handleBulkEntityUpdate}
                        enableBulkEditing={isBulkEditable}
                        popOverPosition={PopoverPlaceEnum.Above}
                        row={row}
                        t={t}
                        clientId={this.props.clientId}
                    />
                );
            }

            return (
                <div
                    title={cellText}
                    testId={cellTestId}
                    className={theme.cellText}
                >
                    {cellText}
                </div>
            );
        };

        getColumns() {
            const { columns, theme } = this.props;
            return columns
                .map(column => {
                    return {
                        Filter: this.getColumnFilterCell(column),
                        ...column,
                        accessorString: column.accessor
                            ? column.accessor
                            : null,
                        Header: this.getHeaderCell,
                        headerClassName: classnames(theme.header, {
                            [column.headerClassName]: column.headerClassName
                        }),
                        CustomCellRenderer: column.Cell,
                        Cell: this.baseCellRenderer,
                        className: classnames(theme.cell, {
                            [column.className]: column.className,
                            [theme.noCellPadding]: column.noCellPadding,
                            [theme.deprecatedColumn]: column.deprecationReason
                        })
                    };
                })
                .filter(column => column.show !== false);
        }

        getTrProps = (tableState, rowInfo, column, tableInstance) => {
            const { getTrProps, theme } = this.props;
            const parentTrProps = getTrProps
                ? getTrProps(tableState, rowInfo, column, tableInstance)
                : {};
            const { className, ...others } = parentTrProps;
            return {
                className: classnames(theme.row, { [className]: !!className }),
                ...others
            };
        };

        getNoDataProps = () => {
            const { getNoDataProps, theme } = this.props;
            const parentTrProps = getNoDataProps ? getNoDataProps() : {};
            const { className, ...others } = parentTrProps;
            return {
                className: classnames(theme.noData, {
                    [className]: !!className
                }),
                ...others
            };
        };

        onPageChange = (nextPageIndex: number) => {
            const { onPageChange } = this.props;
            this.setState({ selectedPageIndex: nextPageIndex });
            onPageChange && onPageChange(nextPageIndex);
        };

        onPageSizeChange = (newPageSize: number) => {
            const { onPageSizeChange } = this.props;
            this.setState({ pageSize: newPageSize, selectedPageIndex: 0 });
            onPageSizeChange && onPageSizeChange(newPageSize);
        };

        onFilteredChange = () => {
            const { onFilteredChange } = this.props;
            this.setState({ selectedPageIndex: 0, scrollToTop: true });
            onFilteredChange && onFilteredChange();
        };

        onFetchData = state => {
            const { onTableStateChange } = this.props;
            onTableStateChange &&
                onTableStateChange({
                    page: state.page,
                    pageSize: state.pageSize,
                    sorted: state.sorted,
                    filtered: state.filtered
                });
        };

        renderPageChooser() {
            const { totalPages, theme, pageSizeOptions } = this.props;
            const { selectedPageIndex, pageSize } = this.state;
            if (typeof totalPages !== 'number') {
                return null;
            }
            return (
                <PageChooser
                    theme={theme}
                    selectedPageIndex={selectedPageIndex}
                    totalPages={totalPages}
                    pageSizeOptions={pageSizeOptions}
                    onPageChange={this.onPageChange}
                    pageSize={pageSize}
                    onPageSizeChange={this.onPageSizeChange}
                />
            );
        }
        getTableRef = ref => {
            const { getTableRef } = this.props;
            this.tableRef = ref;
            getTableRef && getTableRef(ref);
        };

        getLoadingProps = (state, rowInfo, column, instance) => {
            const { getLoadingProps, loaderSize } = this.props;
            const loadingProps =
                (getLoadingProps &&
                    getLoadingProps(state, rowInfo, column, instance)) ||
                {};
            return {
                ...loadingProps,
                loaderSize
            };
        };

        customProps = () => {
            return { testId: entityIds.tableDiv };
        };

        render() {
            const {
                data,
                totalPages,
                enableFiltering,
                hasCheckbox,
                className,
                wrapperClass,
                theme,
                LoadingComponent,
                defaultSorted,
                stickyHeader,
                'data-test-id': dataTestId,
                manual = true
            } = this.props;
            const { selectedPageIndex, pageSize } = this.state;
            return (
                <div className={classnames(theme.containerWrap, wrapperClass)}>
                    <div
                        className={theme.paginatedTableContainer}
                        data-test-id={dataTestId}
                    >
                        <TableComponent
                            {...this.props}
                            innerRef={this.getTableRef}
                            filterable={enableFiltering}
                            manual={manual}
                            className={classnames(theme.table, className, {
                                [theme.hasCheckbox]: hasCheckbox,
                                [theme.nonStickyHeader]: !stickyHeader
                            })}
                            columns={this.getColumns()}
                            onSortedChange={this.onSortedChange}
                            onFilteredChange={this.onFilteredChange}
                            pageSize={pageSize}
                            pages={
                                typeof totalPages === 'number' ? totalPages : -1
                            }
                            showPagination={false}
                            page={selectedPageIndex}
                            getTrProps={this.getTrProps}
                            getTableProps={this.getTableProps}
                            getNoDataProps={this.getNoDataProps}
                            onFetchData={this.onFetchData}
                            LoadingComponent={LoadingComponent}
                            getLoadingProps={this.getLoadingProps}
                            noNesting
                            defaultSorted={defaultSorted}
                            getProps={this.customProps}
                        />
                    </div>
                    {data && data.length ? this.renderPageChooser() : null}
                </div>
            );
        }
    }
    return withUnitConverter(themr('PaginatedTable', theme)(PaginatedTable));
};

const PaginatedTableWithFixedColumns = paginatedTableFactory({
    TableComponent: ReactTableFixedColumns
});

const PaginatedReactTable = paginatedTableFactory({
    TableComponent: ReactTable
});

export default PaginatedTableWithFixedColumns;
export {
    PaginatedTableWithFixedColumns as PaginatedTable,
    PaginatedReactTable
};
